/**
 * Beauty Gables — Shopify Theme JavaScript
 * Version: 1.0.0
 */

'use strict';

/* ============================================
   UTILITIES
   ============================================ */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const fmt = window.beautyGablesConfig?.shopMoneyFormat || '${{amount}}';
  return fmt.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.floor(cents / 100));
}

function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

function fetchJSON(url, opts = {}) {
  return fetch(url, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    ...opts,
  }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
}

/* ============================================
   CART STORE
   ============================================ */
const Cart = {
  async get() {
    return fetchJSON('/cart.js');
  },
  async add(variantId, qty = 1, properties = {}) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ id: variantId, quantity: qty, properties }),
    });
  },
  async change(key, qty) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: qty }),
    });
  },
  async remove(key) {
    return this.change(key, 0);
  },
};

/* ============================================
   CART DRAWER
   ============================================ */
const CartDrawer = (() => {
  const drawer = $('#CartDrawer');
  const overlay = $('#CartOverlay');
  const closeBtn = $('#CartClose');
  const cartEmptyCTA = $('#CartEmptyCTA');

  if (!drawer) return { open() {}, close() {}, update() {} };

  function open() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
  }

  function close() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  }

  async function refresh() {
    const cart = await Cart.get();
    renderItems(cart);
    updateCount(cart.item_count);
    updateTotal(cart.total_price);
    updateShippingBar(cart.total_price);
  }

  function updateCount(count) {
    $$('#CartCount').forEach(el => { el.textContent = count; });
  }

  function updateTotal(cents) {
    $$('#CartTotal, #CartPageTotal, #CartPageTotalFinal').forEach(el => {
      el.textContent = formatMoney(cents);
    });
  }

  function updateShippingBar(totalCents) {
    const threshold = (window.beautyGablesConfig?.freeShippingThreshold || 50) * 100;
    const bar = drawer.querySelector('.cart-drawer__shipping-fill');
    if (bar && threshold > 0) {
      const pct = Math.min((totalCents / threshold) * 100, 100);
      bar.style.width = `${pct}%`;
    }
  }

  function renderItems(cart) {
    const body = $('#CartDrawerBody');
    const footer = drawer.querySelector('.cart-drawer__footer');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty" id="CartEmpty">
          <svg class="cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <p class="cart-drawer__empty-title">Your cart is empty</p>
          <button class="btn btn--primary" id="CartEmptyCTA">Continue Shopping</button>
        </div>`;
      if (footer) footer.style.display = 'none';
      const cta = $('#CartEmptyCTA');
      if (cta) cta.addEventListener('click', close);
    } else {
      body.innerHTML = `<ul class="cart-drawer__items" id="CartItems">
        ${cart.items.map(item => `
          <li class="cart-item" data-key="${item.key}">
            <div class="cart-item__image-wrap">
              <a href="${item.url}">
                ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy" class="cart-item__image">` : ''}
              </a>
            </div>
            <div class="cart-item__details">
              ${item.vendor ? `<p class="cart-item__vendor">${escapeHtml(item.vendor)}</p>` : ''}
              <a href="${item.url}" class="cart-item__name">${escapeHtml(item.product_title)}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
              <div class="cart-item__bottom">
                <div class="cart-item__qty">
                  <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </button>
                  <span class="qty-number" id="qty-${item.key}">${item.quantity}</span>
                  <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </button>
                </div>
                <span class="cart-item__price">${formatMoney(item.line_price)}</span>
              </div>
            </div>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </li>`).join('')}
      </ul>`;
      if (footer) {
        footer.style.display = '';
        const checkoutBtn = footer.querySelector('.cart-drawer__checkout');
        if (checkoutBtn) checkoutBtn.href = '/checkout';
      }
      bindCartItemActions();
    }

    const countEl = drawer.querySelector('.cart-drawer__count');
    if (countEl) countEl.textContent = `(${cart.item_count})`;
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  function bindCartItemActions() {
    $$('.qty-btn', drawer).forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const qtyEl = $(`#qty-${key}`, drawer) || $(`#qty-${key}`);
        let currentQty = parseInt(qtyEl?.textContent || '1');
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        btn.disabled = true;
        try {
          await Cart.change(key, newQty);
          await refresh();
        } finally {
          btn.disabled = false;
        }
      });
    });

    $$('.cart-item__remove', drawer).forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const item = btn.closest('.cart-item');
        if (item) { item.style.opacity = '.4'; item.style.pointerEvents = 'none'; }
        try {
          await Cart.remove(key);
          await refresh();
        } catch {
          if (item) { item.style.opacity = ''; item.style.pointerEvents = ''; }
        }
      });
    });
  }

  // Events
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);
  cartEmptyCTA?.addEventListener('click', close);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  // Cart toggle — event delegation covers BOTH mobile and desktop buttons
  // (Both share class js-cart-toggle; using delegation avoids duplicate-ID bug)
  document.addEventListener('click', e => {
    if (e.target.closest('.js-cart-toggle')) {
      if (window.beautyGablesConfig?.cartType === 'drawer') {
        open();
      } else {
        window.location.href = '/cart';
      }
    }
  });

  // Cart page qty buttons
  $$(`.cart-item__qty .qty-btn`).forEach(btn => {
    if (!drawer.contains(btn)) {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const qtyEl = $(`#qty-${key}`);
        let currentQty = parseInt(qtyEl?.textContent || '1');
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        await Cart.change(key, newQty);
        window.location.reload();
      });
    }
  });

  // Cart page remove
  $$('.cart-item__remove:not(#CartDrawer .cart-item__remove)').forEach(btn => {
    btn.addEventListener('click', async () => {
      await Cart.remove(btn.dataset.key);
      window.location.reload();
    });
  });

  return { open, close, refresh };
})();

/* ============================================
   ADD TO CART
   ============================================ */
async function addToCart(variantId, btn, qty = 1) {
  btn.classList.add('is-loading');
  btn.disabled = true;

  try {
    await Cart.add(variantId, qty);

    if (window.beautyGablesConfig?.cartType === 'drawer') {
      await CartDrawer.refresh();
      CartDrawer.open();
    }

    // Success animation
    const card = btn.closest('.product-card');
    if (card) {
      card.classList.add('product-card--added');
      setTimeout(() => card.classList.remove('product-card--added'), 2000);
    }

    // Product page success
    const label = btn.querySelector('.product-form__atc-label');
    if (label) {
      const originalText = label.textContent;
      label.textContent = '✓ Added to Cart!';
      btn.style.background = '#10b981';
      setTimeout(() => {
        label.textContent = originalText;
        btn.style.background = '';
      }, 2200);
    }

  } catch (err) {
    console.error('Add to cart failed:', err);
    const label = btn.querySelector('.product-form__atc-label, .product-card__add-label');
    if (label) {
      const originalText = label.textContent;
      label.textContent = 'Error — try again';
      btn.style.background = '#ef4444';
      setTimeout(() => { label.textContent = originalText; btn.style.background = ''; }, 2000);
    }
  } finally {
    btn.classList.remove('is-loading');
    btn.disabled = false;
  }
}

// Product card "Add to Cart" buttons
document.addEventListener('click', e => {
  const btn = e.target.closest('.product-card__add-to-cart[data-variant-id]');
  if (!btn || btn.disabled) return;
  e.preventDefault();
  addToCart(btn.dataset.variantId, btn);
});

// Product page form
const productForm = $('#ProductForm');
if (productForm) {
  productForm.addEventListener('submit', e => {
    e.preventDefault();
    const variantInput = productForm.querySelector('[name="id"]');
    const qtyInput = productForm.querySelector('[name="quantity"]');
    const submitBtn = productForm.querySelector('[type="submit"]');
    if (!variantInput || !submitBtn) return;
    const variantId = variantInput.value;
    const qty = parseInt(qtyInput?.value || '1');
    addToCart(variantId, submitBtn, qty);
  });
}

/* ============================================
   PRODUCT GALLERY
   ============================================ */
const productGallery = (() => {
  const mainImg = $('#ProductMainImg');
  const thumbs = $$('.product-gallery__thumb');

  if (!mainImg || !thumbs.length) return;

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.imgSrc;
      const alt = thumb.dataset.imgAlt;
      mainImg.style.opacity = '0';
      setTimeout(() => {
        mainImg.src = src;
        mainImg.alt = alt;
        mainImg.style.opacity = '1';
      }, 200);
      thumbs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-selected', 'true');
    });
    thumb.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); thumb.click(); }
    });
  });
})();

/* ============================================
   PRODUCT TABS
   ============================================ */
const productTabs = (() => {
  const tabNav = $('#ProductTabs');
  if (!tabNav) return;

  const buttons = $$('.product-tabs__btn', tabNav);
  const panels = $$('.product-tabs__panel', tabNav);

  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      buttons.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
      panels.forEach(p => p.classList.remove('is-active'));
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      const panel = $(`#${btn.getAttribute('aria-controls')}`);
      if (panel) panel.classList.add('is-active');
    });
  });
})();

/* ============================================
   PRODUCT QUANTITY CONTROLS
   ============================================ */
const qtyControls = (() => {
  const increaseBtn = $('#QtyIncrease');
  const decreaseBtn = $('#QtyDecrease');
  const qtyInput = $('#ProductQty');
  if (!increaseBtn || !decreaseBtn || !qtyInput) return;

  increaseBtn.addEventListener('click', () => {
    qtyInput.value = parseInt(qtyInput.value) + 1;
  });
  decreaseBtn.addEventListener('click', () => {
    const val = parseInt(qtyInput.value);
    if (val > 1) qtyInput.value = val - 1;
  });
})();

/* ============================================
   VARIANT SELECTOR
   ============================================ */
const variantSelector = (() => {
  const optionBtns = $$('.product-form__option-btn');
  const variantInput = $('#ProductVariantId');
  const atcBtn = $('#AddToCartBtn');
  const priceEl = $('.product-details__price');

  if (!optionBtns.length || !variantInput) return;

  const productData = window.__productData;

  optionBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const pos = btn.dataset.optionPosition;
      const siblings = $$(`.product-form__option-btn[data-option-position="${pos}"]`);
      siblings.forEach(s => { s.classList.remove('is-active'); s.setAttribute('aria-pressed', 'false'); });
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');

      const labelValue = $(`#OptionValue-${pos}`);
      if (labelValue) labelValue.textContent = btn.dataset.value;
    });
  });
})();

/* ============================================
   PRODUCT RECOMMENDATIONS (AJAX)
   ============================================ */
const productRecs = (() => {
  const section = document.querySelector('[id^="ProductRecommendations-"]');
  if (!section) return;

  const productId = section.dataset.productId;
  const sectionId = section.dataset.sectionId;
  const limit     = section.dataset.limit || 4;
  const grid      = section.querySelector('#ProductRecsGrid');
  if (!productId || !sectionId || !grid) return;

  const url = `/recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}`;

  fetch(url)
    .then(r => {
      if (!r.ok) throw new Error('fetch failed');
      return r.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc    = parser.parseFromString(html, 'text/html');
      /* Shopify wraps the section in a div — grab cards inside the recs grid */
      const cards  = doc.querySelectorAll('.product-card');
      if (cards.length === 0) {
        section.style.display = 'none';
        return;
      }
      grid.innerHTML = '';
      cards.forEach(card => grid.appendChild(card));
      /* Re-attach quick-view / quick-add events for newly inserted cards */
      if (typeof initProductCardEvents === 'function') initProductCardEvents(grid);
    })
    .catch(() => { section.style.display = 'none'; });
})();


/* ============================================
   SLIDESHOW
   ============================================ */
document.querySelectorAll('[id^="Slideshow-"]').forEach(slideshow => {
  const id = slideshow.id.replace('Slideshow-', '');
  const slides = $$('.slideshow__slide', slideshow);
  const dots = $$('.slideshow__dot', slideshow);
  const prevBtn = $(`#SlideshowPrev-${id}`);
  const nextBtn = $(`#SlideshowNext-${id}`);
  if (slides.length <= 1) return;

  let current = 0;
  let autoplay = null;
  const autoplayDelay = parseInt(slideshow.closest('[data-autoplay]')?.dataset.autoplay || '6000');

  function goTo(idx, direction = 'next') {
    slides[current].classList.remove('is-active');
    dots[current]?.classList.remove('is-active');
    dots[current]?.setAttribute('aria-selected', 'false');
    current = (idx + slides.length) % slides.length;
    slides[current].classList.add('is-active');
    dots[current]?.classList.add('is-active');
    dots[current]?.setAttribute('aria-selected', 'true');
  }

  function startAutoplay() {
    if (!autoplayDelay || autoplayDelay === 0) return;
    autoplay = setInterval(() => goTo(current + 1), autoplayDelay);
  }

  function stopAutoplay() { clearInterval(autoplay); }

  nextBtn?.addEventListener('click', () => { stopAutoplay(); goTo(current + 1); startAutoplay(); });
  prevBtn?.addEventListener('click', () => { stopAutoplay(); goTo(current - 1); startAutoplay(); });

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => { stopAutoplay(); goTo(i); startAutoplay(); });
  });

  // Touch swipe
  let touchStartX = 0;
  slideshow.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
  slideshow.addEventListener('touchend', e => {
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { stopAutoplay(); goTo(diff > 0 ? current + 1 : current - 1); startAutoplay(); }
  });

  startAutoplay();
});

/* ============================================
   HEADER SCROLL BEHAVIOR
   ============================================ */
const siteHeader = $('#SiteHeader');
if (siteHeader) {
  const scrollHandler = () => {
    if (window.scrollY > 20) {
      siteHeader.classList.add('is-scrolled');
    } else {
      siteHeader.classList.remove('is-scrolled');
    }
  };
  window.addEventListener('scroll', scrollHandler, { passive: true });
  scrollHandler();
}

/* ============================================
   MOBILE MENU
   ============================================ */
const mobileMenu = (() => {
  const menu = $('#MobileMenu');
  const overlay = $('#MobileMenuOverlay');
  const openBtn = $('#MobileMenuToggle');
  const closeBtn = $('#MobileMenuClose');
  if (!menu) return;

  function open() {
    menu.classList.add('is-open');
    overlay.classList.add('is-visible');
    menu.setAttribute('aria-hidden', 'false');
    openBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    menu.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    menu.setAttribute('aria-hidden', 'true');
    openBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  openBtn?.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);

  $$('.mobile-menu__accordion-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const subList = btn.nextElementSibling;
      const isOpen = !subList.hidden;
      subList.hidden = isOpen;
      btn.classList.toggle('is-open', !isOpen);
      btn.setAttribute('aria-expanded', (!isOpen).toString());
    });
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && menu.classList.contains('is-open')) close();
  });
})();

/* ============================================
   DESKTOP NAV DROPDOWNS — click toggle
   Allows users to click the chevron button to open/close dropdowns.
   CSS :hover still works for mouse users; JS adds click/touch support.
   ============================================ */
(function() {
  // Toggle dropdown on chevron button click
  document.addEventListener('click', function(e) {
    const toggle = e.target.closest('.site-header__dropdown-toggle');
    const helpBtn = e.target.closest('.site-header__help-btn');

    if (toggle) {
      e.preventDefault();
      e.stopPropagation();
      const item = toggle.closest('.site-header__nav-item');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      // Close all open nav dropdowns
      $$('.site-header__nav-item.is-open').forEach(i => {
        i.classList.remove('is-open');
        const t = i.querySelector('.site-header__dropdown-toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      // Toggle current
      if (!isOpen) {
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (helpBtn) {
      e.preventDefault();
      e.stopPropagation();
      const helpWrap = helpBtn.closest('.site-header__help');
      if (!helpWrap) return;
      const isOpen = helpWrap.classList.contains('is-open');
      helpWrap.classList.toggle('is-open', !isOpen);
      helpBtn.setAttribute('aria-expanded', (!isOpen).toString());
      return;
    }

    // Click outside — close all dropdowns
    if (!e.target.closest('.site-header__nav-item.has-dropdown') && !e.target.closest('.site-header__help.has-dropdown')) {
      $$('.site-header__nav-item.is-open').forEach(i => {
        i.classList.remove('is-open');
        const t = i.querySelector('.site-header__dropdown-toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      $$('.site-header__help.is-open').forEach(h => {
        h.classList.remove('is-open');
        const b = h.querySelector('.site-header__help-btn');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    }
  });

  // Close on Escape
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      $$('.site-header__nav-item.is-open').forEach(i => {
        i.classList.remove('is-open');
        const t = i.querySelector('.site-header__dropdown-toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      $$('.site-header__help.is-open').forEach(h => {
        h.classList.remove('is-open');
      });
    }
  });
})();

/* ============================================
   SEARCH DRAWER — handled by searchInit() below
   ============================================ */

/* ============================================
   PARALLAX SCROLLING
   ============================================ */
const parallaxInit = () => {
  const parallaxSections = $$('[data-parallax="true"]');
  if (!parallaxSections.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function update() {
    parallaxSections.forEach(section => {
      const rect = section.getBoundingClientRect();
      const img = section.querySelector('[data-parallax-img]');
      if (!img) return;
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (!visible) return;
      const speed = 0.25;
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * speed;
      img.style.transform = `translateY(${offset}px)`;
    });
  }

  window.addEventListener('scroll', update, { passive: true });
  update();
};

/* ============================================
   SCROLL ANIMATIONS (Intersection Observer)
   ============================================ */
const animationInit = () => {
  const elements = $$('[data-animate]');
  if (!elements.length) return;

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const delay = entry.target.dataset.animateDelay || 0;
        setTimeout(() => entry.target.classList.add('is-visible'), delay);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  elements.forEach(el => observer.observe(el));
};

/* ============================================
   COLLECTION FILTERS
   ============================================ */
const collectionFilters = (() => {
  const form = $('#CollectionFiltersForm');
  const sortSelect = $('#SortBy');
  const viewBtns = $$('.collection-view-toggle__btn');
  const productGrid = $('#ProductGrid');
  const clearBtn = $('#ClearFilters');

  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  if (viewBtns.length && productGrid) {
    viewBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        viewBtns.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        productGrid.dataset.view = view;
      });
    });
  }

  // Filter accordion toggles
  $$('.collection-filters__group-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';
      const body = btn.nextElementSibling;
      btn.setAttribute('aria-expanded', (!isExpanded).toString());
      if (body) body.style.display = isExpanded ? 'none' : '';
    });
  });

  // Filter checkboxes auto-submit
  $$('.collection-filters__checkbox').forEach(checkbox => {
    checkbox.addEventListener('change', debounce(() => {
      const url = new URL(window.location.href);
      $$('.collection-filters__checkbox').forEach(cb => {
        if (cb.checked) {
          url.searchParams.append(cb.name, cb.value);
        }
      });
      window.location.href = url.toString();
    }, 500));
  });

  clearBtn?.addEventListener('click', () => {
    window.location.href = window.location.pathname;
  });
})();

/* ============================================
   STATS COUNTER ANIMATION
   ============================================ */
const statsCounter = (() => {
  const stats = $$('.stats-bar__number[data-count]');
  if (!stats.length) return;

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const target = parseFloat(el.dataset.count) || 0;
      const originalText = el.textContent;
      const isDecimal = originalText.includes('.');
      const suffix = originalText.replace(/[\d.,]/g, '');
      const prefix = suffix.startsWith('/') ? '' : '';

      let start = 0;
      const duration = 2000;
      const step = timestamp => {
        if (!start) start = timestamp;
        const progress = Math.min((timestamp - start) / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        const value = isDecimal ? (ease * target).toFixed(1) : Math.floor(ease * target);
        el.textContent = value.toLocaleString() + suffix;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      observer.unobserve(el);
    });
  }, { threshold: 0.5 });

  stats.forEach(el => observer.observe(el));
})();

/* ============================================
   WISHLIST (local storage toggle)
   ============================================ */
const wishlistInit = () => {
  const wishlist = JSON.parse(localStorage.getItem('bg-wishlist') || '[]');

  function toggle(productId) {
    const idx = wishlist.indexOf(productId);
    if (idx >= 0) wishlist.splice(idx, 1);
    else wishlist.push(productId);
    localStorage.setItem('bg-wishlist', JSON.stringify(wishlist));
    return wishlist.includes(productId);
  }

  function syncUI() {
    $$('.product-card__wishlist, .product-form__wishlist-btn').forEach(btn => {
      const card = btn.closest('[data-product-id]');
      if (!card) return;
      const productId = card.dataset.productId;
      btn.classList.toggle('is-active', wishlist.includes(productId));
    });
  }

  document.addEventListener('click', e => {
    const btn = e.target.closest('.product-card__wishlist, .product-form__wishlist-btn');
    if (!btn) return;
    const card = btn.closest('[data-product-id]');
    if (!card) return;
    const productId = card.dataset.productId;
    const isNowActive = toggle(productId);
    btn.classList.toggle('is-active', isNowActive);
  });

  syncUI();
};

/* ============================================
   SEARCH DRAWER (simple — no predictive)
   ============================================ */
const searchInit = () => {
  const drawer  = document.getElementById('SearchDrawer');
  const overlay = document.getElementById('SearchOverlay');
  const input   = document.getElementById('SearchInput');
  const closeBtn= document.getElementById('SearchClose');

  if (!drawer) return;

  function openSearch() {
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-visible');
    drawer.removeAttribute('aria-hidden');
    document.body.classList.add('no-scroll');
    setTimeout(() => input && input.focus(), 200);
  }

  function closeSearch() {
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-visible');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  }

  // Open — any search trigger button in the page
  document.addEventListener('click', e => {
    if (e.target.closest('#SearchToggle, .search-drawer__trigger, [id^="SearchToggle"]')) {
      openSearch();
    }
  });

  if (closeBtn) closeBtn.addEventListener('click', closeSearch);
  if (overlay)  overlay.addEventListener('click', closeSearch);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeSearch();
  });
};

/* ============================================
   INIT
   ============================================ */
document.addEventListener('DOMContentLoaded', () => {
  parallaxInit();
  animationInit();
  wishlistInit();
  searchInit();

  // Add data-animate to section headers
  $$('.section-header, .product-card, .category-card, .testimonial-card, .blog-card, .stats-bar__item').forEach((el, i) => {
    el.setAttribute('data-animate', '');
    el.setAttribute('data-animate-delay', Math.min(i % 4 * 80, 300));
  });

  // Re-run animations for newly added animate attributes
  animationInit();
});

/* ============================================
   PAGE TRANSITIONS
   ============================================ */
const pageTransitionInit = () => {
  const overlay = document.getElementById('PageTransitionOverlay');
  if (!overlay) return;

  // Intercept navigation clicks for a smooth fade-out
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');

    // Skip: external, hash, mailto, tel, javascript, target=_blank
    if (!href) return;
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
    if (link.target === '_blank') return;
    if (link.hostname && link.hostname !== window.location.hostname) return;
    // Skip drawer toggles / cart links handled via JS
    if (link.dataset.action) return;
    if (link.classList.contains('site-footer__social-link')) return;

    e.preventDefault();
    const dest = link.href;

    // Fade out
    overlay.classList.add('fade-out');
    setTimeout(() => {
      window.location.href = dest;
    }, 280);
  });

  // Handle browser back/forward
  window.addEventListener('pageshow', () => {
    overlay.classList.remove('fade-out');
  });
};

// Re-init on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  pageTransitionInit();
});

/* ============================================================
   SMART SEARCH OVERLAY
   Full-screen search with popular terms + live Shopify results
   ============================================================ */
(function() {

  const overlay    = document.getElementById('SearchOverlay');
  const backdropEl = document.getElementById('SearchOverlayBackdrop');
  const inputEl    = document.getElementById('SearchOverlayInput');
  const closeBtn   = document.getElementById('SearchOverlayClose');
  const submitLink = document.getElementById('SearchOverlaySubmit');
  const headerBar  = document.getElementById('HeaderSearchBar');
  const headerInput = document.getElementById('HeaderSearchInput');

  const popularSection  = document.getElementById('SearchPopularSection');
  const featuredSection = document.getElementById('SearchFeaturedSection');
  const resultsSection  = document.getElementById('SearchResultsSection');
  const resultsGrid     = document.getElementById('SearchResultsGrid');
  const resultsTitle    = document.getElementById('SearchResultsTitle');
  const featuredGrid    = document.getElementById('SearchFeaturedGrid');
  const seeAll          = document.getElementById('SearchSeeAll');

  if (!overlay || !inputEl) return;

  let featuredLoaded = false;

  /* ─── Open / Close ─────────────────────────────────────── */
  window.openSearchOverlay = function() {
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      inputEl.focus();
    });
    if (!featuredLoaded) loadFeaturedProducts();
    // Reset: show popular+featured, hide results
    showDefaultState();
  };

  function closeSearchOverlay() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => { overlay.hidden = true; }, 280);
    inputEl.value = '';
    showDefaultState();
  }

  function showDefaultState() {
    if (popularSection)  popularSection.hidden  = false;
    if (featuredSection) featuredSection.hidden = false;
    if (resultsSection)  resultsSection.hidden  = true;
  }

  function showResultsState() {
    if (popularSection)  popularSection.hidden  = true;
    if (featuredSection) featuredSection.hidden = true;
    if (resultsSection)  resultsSection.hidden  = false;
  }

  /* ─── Triggers ─────────────────────────────────────────── */
  if (closeBtn) closeBtn.addEventListener('click', closeSearchOverlay);
  if (backdropEl) backdropEl.addEventListener('click', closeSearchOverlay);

  // Header inline search bar click / focus
  if (headerBar)  headerBar.addEventListener('click',  window.openSearchOverlay);
  if (headerInput) headerInput.addEventListener('focus', window.openSearchOverlay);

  // Close on Escape
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && !overlay.hidden) closeSearchOverlay();
  });

  // Submit link: update href when input changes
  if (inputEl && submitLink) {
    inputEl.addEventListener('input', function() {
      if (seeAll) seeAll.href = '/search?q=' + encodeURIComponent(inputEl.value.trim()) + '&type=product';
      if (submitLink) submitLink.href = '/search?q=' + encodeURIComponent(inputEl.value.trim()) + '&type=product';
    });
    inputEl.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const q = inputEl.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q) + '&type=product';
      }
    });
  }

  /* ─── Live search (debounced) ───────────────────────────── */
  const doSearch = debounce(async function(query) {
    if (!query || query.length < 2) { showDefaultState(); return; }
    showResultsState();
    resultsGrid.innerHTML = '<div class="search-overlay__loading"><span class="search-overlay__spinner"></span></div>';
    if (resultsTitle) resultsTitle.textContent = 'RESULTS FOR "' + query.toUpperCase() + '"';

    try {
      const url = '/search/suggest.json?q=' + encodeURIComponent(query)
        + '&resources[type]=product&resources[limit]=8';
      const data = await fetch(url).then(r => r.json());
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];

      if (products.length === 0) {
        resultsGrid.innerHTML = '<p class="search-overlay__no-results">No products found for "' + query + '"</p>';
        return;
      }
      resultsGrid.innerHTML = '';
      products.forEach(function(p) {
        resultsGrid.appendChild(buildSearchProductCard(p));
      });
    } catch(e) {
      resultsGrid.innerHTML = '<p class="search-overlay__no-results">Search unavailable. <a href="/search?q=' + encodeURIComponent(query) + '">Try full search →</a></p>';
    }
  }, 320);

  if (inputEl) {
    inputEl.addEventListener('input', function() {
      doSearch(inputEl.value.trim());
    });
  }

  /* ─── Build search result product card ─────────────────── */
  function buildSearchProductCard(p) {
    const href  = p.url || ('/products/' + (p.handle || ''));
    const title = p.title || '';
    const vendor = p.vendor || '';
    const price  = p.price ? formatMoney(parseInt(p.price, 10) * 100) : '';
    const img    = (p.featured_image && p.featured_image.url) ? p.featured_image.url.replace('{width}', '300') : '';

    const a = document.createElement('a');
    a.href = href;
    a.className = 'search-overlay__product-card';
    a.innerHTML =
      '<div class="search-overlay__product-img-wrap">' +
        (img ? '<img src="' + img + '" alt="' + title.replace(/"/g,'&quot;') + '" class="search-overlay__product-img" loading="lazy" width="150" height="150">' : '') +
      '</div>' +
      (vendor ? '<span class="search-overlay__product-vendor">' + vendor + '</span>' : '') +
      '<span class="search-overlay__product-title">' + title + '</span>' +
      (price ? '<span class="search-overlay__product-price">' + price + '</span>' : '');
    return a;
  }

  /* ─── Featured / popular products ──────────────────────── */
  async function loadFeaturedProducts() {
    if (!featuredGrid) return;
    featuredLoaded = true;
    try {
      const data = await fetch('/collections/all/products.json?sort_by=best-selling&limit=4').then(r => r.json());
      const products = data.products || [];
      if (products.length === 0) { featuredGrid.innerHTML = ''; return; }
      featuredGrid.innerHTML = '';
      products.forEach(function(p) {
        featuredGrid.appendChild(buildFeaturedCard(p));
      });
    } catch(e) {
      featuredGrid.innerHTML = '';
    }
  }

  function buildFeaturedCard(p) {
    const href  = '/products/' + p.handle;
    const title = p.title || '';
    const vendor = p.vendor || '';
    const variant = p.variants && p.variants[0];
    const price  = variant ? formatMoney(parseFloat(variant.price) * 100) : '';
    const img    = p.images && p.images[0] ? p.images[0].src : '';
    const imgSrc = img ? img.replace(/\.(jpg|jpeg|png|gif|webp)/, '_400x400.$1') : '';

    const a = document.createElement('a');
    a.href = href;
    a.className = 'search-overlay__product-card';
    a.innerHTML =
      '<div class="search-overlay__product-img-wrap">' +
        (imgSrc ? '<img src="' + imgSrc + '" alt="' + title.replace(/"/g,'&quot;') + '" class="search-overlay__product-img" loading="lazy" width="200" height="200">' : '') +
      '</div>' +
      (vendor ? '<span class="search-overlay__product-vendor">' + vendor + '</span>' : '') +
      '<span class="search-overlay__product-title">' + title + '</span>' +
      (price ? '<span class="search-overlay__product-price">' + price + '</span>' : '');
    return a;
  }

})();

/* ============================================================
   STICKY HEADER — visible 2 s al bajar, reaparece al subir
   ============================================================ */
(function () {
  var header = document.getElementById('SiteHeader');
  if (!header || !header.classList.contains('site-header--sticky')) return;

  var lastY      = window.scrollY;
  var hideTimer  = null;
  var headerH    = header.offsetHeight;

  function showHeader() {
    clearTimeout(hideTimer);
    header.classList.remove('site-header--hidden');
  }

  function hideAfterDelay() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      header.classList.add('site-header--hidden');
    }, 2000);
  }

  window.addEventListener('scroll', function () {
    var currentY = window.scrollY;

    // At the very top → always show, cancel any pending hide
    if (currentY <= headerH) {
      showHeader();
      lastY = currentY;
      return;
    }

    if (currentY > lastY) {
      // Scrolling DOWN: show briefly then hide
      showHeader();
      hideAfterDelay();
    } else {
      // Scrolling UP: show immediately, cancel hide
      showHeader();
      clearTimeout(hideTimer);
    }

    lastY = currentY;
  }, { passive: true });
})();


/* ============================================================
   STICKY HEADER v2 — anima el .site-header-sticky-wrap con top
   · Scroll abajo  → visible 2 s, luego sube y se oculta
   · Scroll arriba → reaparece de inmediato
   · En el tope de la página → siempre visible
   ============================================================ */
document.addEventListener('DOMContentLoaded', function () {

  var wrap   = document.querySelector('.site-header-sticky-wrap');
  if (!wrap) return;

  var lastY      = window.scrollY;
  var hideTimer  = null;
  var isHidden   = false;
  var wrapH      = 0;

  /* Calcula la altura real del wrapper (header + announcement) */
  function getWrapHeight() {
    return wrap.offsetHeight || 120;
  }

  function showWrap() {
    clearTimeout(hideTimer);
    if (!isHidden) return;
    wrap.style.top = '0px';
    isHidden = false;
  }

  function hideWrap() {
    if (isHidden) return;
    wrapH = getWrapHeight();
    wrap.style.top = '-' + wrapH + 'px';
    isHidden = true;
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideWrap, 2000);
  }

  window.addEventListener('scroll', function () {
    var currentY = window.scrollY;

    /* Al tope de la página: siempre mostrar */
    if (currentY <= 10) {
      clearTimeout(hideTimer);
      showWrap();
      lastY = currentY;
      return;
    }

    if (currentY > lastY + 4) {
      /* Scrolleando hacia abajo */
      showWrap();
      scheduleHide();
    } else if (currentY < lastY - 4) {
      /* Scrolleando hacia arriba */
      clearTimeout(hideTimer);
      showWrap();
    }

    lastY = currentY;
  }, { passive: true });

  /* Recalcular si cambia el tamaño de la ventana */
  window.addEventListener('resize', function () {
    if (isHidden) {
      wrapH = getWrapHeight();
      wrap.style.top = '-' + wrapH + 'px';
    }
  });

});

